import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { trackPageView } from './services/analytics';
import api from './services/api';
import Navbar from './components/Navbar';
import SwipeNavigator from './components/SwipeNavigator';
import DisclaimerModal from './components/DisclaimerModal';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AskNitin from './pages/AskNitin';
import ResumeBuilder from './pages/ResumeBuilder';
import Account from './pages/Account';
import Admin from './pages/Admin';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminChat from './pages/AdminChat';
import Assessment from './pages/Assessment';
import AssessmentResult from './pages/AssessmentResult';
import About from './pages/About';
import Sprint from './pages/Sprint';
import DirectChatBubble from './components/DirectChatBubble';
import SprintTask1 from './pages/SprintTask1';
import SprintTask2 from './pages/SprintTask2';
import CDP from './pages/CDP';
import CDPDocument from './pages/CDPDocument';
import CDPCertificate from './pages/CDPCertificate';
import CDPCertificateVerify from './pages/CDPCertificateVerify';
import MockInterview from './pages/MockInterview';
import ResumeAssessment from './pages/ResumeAssessment';
import FeedbackPage from './pages/FeedbackPage';
import DashboardV2 from './pages/DashboardV2';
import GuidedChat from './pages/GuidedChat';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !['admin', 'superadmin'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Loader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: 'var(--bg)',
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%',
        border: '2.5px solid var(--border)', borderTop: '2.5px solid var(--navy)',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
}

function ProfileGate() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const needsProfile = user && (!user.name || !user.email || !user.linkedin);

  // Extract username from full LinkedIn URL
  const extractUsername = (val) => {
    const match = val.match(/linkedin\.com\/in\/([a-zA-Z0-9._-]+)\/?/);
    if (match) return match[1];
    return '';
  };

  useEffect(() => {
    if (user) {
      if (user.name) setName(user.name);
      if (user.email) setEmail(user.email);
      if (user.linkedin) {
        setLinkedin(`https://www.linkedin.com/in/${user.linkedin}`);
      }
    }
  }, [user]);

  if (!needsProfile) return null;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const usernameRegex = /^[a-zA-Z0-9_-]{3,100}$/;

  const validate = () => {
    if (!name.trim()) return 'Please enter your full name';
    if (!email.trim()) return 'Please enter your email address';
    if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
    const username = extractUsername(linkedin);
    if (!username) return 'Please paste your LinkedIn profile URL (e.g. https://www.linkedin.com/in/your-username)';
    if (!usernameRegex.test(username)) return 'Could not find a valid username in that URL. Please paste the full LinkedIn profile URL';
    return '';
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) return setError(err);
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const username = extractUsername(linkedin);
      await api.put('/user/profile', {
        name: name.trim(),
        email: email.trim(),
        linkedin: username,
      });
      await refreshUser();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save profile');
    }
    setSaving(false);
  };

  return (
    <div style={profileGateStyles.overlay}>
      <div style={profileGateStyles.modal}>
        <div style={profileGateStyles.icon}>👋</div>
        <h2 style={profileGateStyles.title}>Complete Your Profile</h2>
        <p style={profileGateStyles.desc}>Please fill in your details to continue. All fields are required.</p>
        <div style={profileGateStyles.field}>
          <label style={profileGateStyles.label}>Full Name</label>
          <input
            style={profileGateStyles.input}
            type="text"
            placeholder="e.g. Rahul Sharma"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div style={profileGateStyles.field}>
          <label style={profileGateStyles.label}>Email Address</label>
          <input
            style={profileGateStyles.input}
            type="email"
            placeholder="e.g. rahul@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div style={profileGateStyles.field}>
          <label style={profileGateStyles.label}>LinkedIn Profile URL</label>
          <input
            style={profileGateStyles.input}
            type="text"
            placeholder="https://www.linkedin.com/in/your-username"
            value={linkedin}
            onChange={e => setLinkedin(e.target.value)}
          />
          <p style={profileGateStyles.hint}>Go to your LinkedIn profile, copy the URL from your browser, and paste it here</p>
        </div>
        {error && <p style={profileGateStyles.error}>{error}</p>}
        <button
          style={{ ...profileGateStyles.btn, opacity: saving ? 0.45 : 1 }}
          disabled={saving}
          onClick={handleSubmit}
        >
          {saving ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

const profileGateStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,35,64,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal: { background: '#fff', borderRadius: '20px', padding: '36px 32px', maxWidth: '440px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '12px', boxShadow: '0 8px 40px rgba(12,35,64,0.18)' },
  icon: { fontSize: '40px', textAlign: 'center' },
  title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: '700', color: '#0c2340', margin: 0, textAlign: 'center' },
  desc: { fontSize: '13px', color: '#3d5166', margin: 0, textAlign: 'center', lineHeight: '1.5' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: '700', color: '#0c2340', fontFamily: "'DM Sans', sans-serif" },
  input: { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: '13px', color: '#374151', margin: '4px 0 0', fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
  error: { fontSize: '13px', color: '#b91c1c', margin: 0, background: '#fef2f2', padding: '8px 12px', borderRadius: '8px', textAlign: 'center' },
  btn: { width: '100%', background: '#0c2340', color: '#fff', border: 'none', padding: '13px', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginTop: '4px' },
};

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed / standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check if dismissed recently (don't show again for 7 days)
    const dismissed = localStorage.getItem('asknitin_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIos(ios);

    if (ios) {
      setShowBanner(true);
      return;
    }

    // Android / Chrome — capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('asknitin_install_dismissed', Date.now().toString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div style={installStyles.banner}>
      <div style={installStyles.inner}>
        <img src="/Ask Nitin Icon V1.png" alt="AskNitin" style={installStyles.icon} />
        <div style={installStyles.text}>
          <p style={installStyles.title}>Install AskNitin</p>
          {isIos ? (
            <p style={installStyles.desc}>
              Tap <strong>Share</strong> <span style={{ fontSize: '15px' }}>&#x2B06;&#xFE0F;</span> then <strong>"Add to Home Screen"</strong>
            </p>
          ) : (
            <p style={installStyles.desc}>Add to your home screen for quick access</p>
          )}
        </div>
        {!isIos && (
          <button onClick={handleInstall} style={installStyles.btn}>Install</button>
        )}
        <button onClick={handleDismiss} style={installStyles.close}>&times;</button>
      </div>
    </div>
  );
}

const installStyles = {
  banner: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
    background: '#fff', borderTop: '1px solid var(--border)',
    boxShadow: '0 -4px 20px rgba(12,35,64,0.12)',
    padding: '12px 16px',
    animation: 'fadeUp 0.3s ease',
  },
  inner: {
    maxWidth: '600px', margin: '0 auto',
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  icon: { width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0 },
  text: { flex: 1, minWidth: 0 },
  title: { fontSize: '14px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  desc: { fontSize: '12px', color: 'var(--text-2)', margin: '2px 0 0', lineHeight: '1.4' },
  btn: {
    background: 'var(--navy)', color: '#fff', border: 'none',
    padding: '8px 18px', borderRadius: '8px', fontSize: '13px',
    fontWeight: '700', cursor: 'pointer', flexShrink: 0,
    fontFamily: "'DM Sans', sans-serif",
  },
  close: {
    background: 'none', border: 'none', fontSize: '20px',
    color: 'var(--text-3)', cursor: 'pointer', padding: '4px 8px',
    flexShrink: 0, lineHeight: 1,
  },
};

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;

  return (
    <>
      <ScrollToTop />
      <PageTracker />
      {user && (!user.name || !user.email || !user.linkedin) && <ProfileGate />}
      {user && user.name && user.email && user.linkedin && !user.disclaimerAccepted && <DisclaimerModal />}
      {user && <Navbar />}
      {user && !['admin', 'superadmin'].includes(user.role) && <DirectChatBubble />}
      <InstallPrompt />
      <SwipeNavigator>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/cdp/certificate/:certNo" element={<CDPCertificateVerify />} />
        <Route path="/" element={<ProtectedRoute><DashboardV2 /></ProtectedRoute>} />
        <Route path="/chat/:module" element={<ProtectedRoute><GuidedChat /></ProtectedRoute>} />
        <Route path="/classic" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/ask" element={<ProtectedRoute><AskNitin /></ProtectedRoute>} />
        <Route path="/resume" element={<ProtectedRoute><ResumeBuilder /></ProtectedRoute>} />
        <Route path="/resume/new" element={<ProtectedRoute><ResumeBuilder /></ProtectedRoute>} />
        <Route path="/resume/:id" element={<ProtectedRoute><ResumeBuilder /></ProtectedRoute>} />
        <Route path="/resume/:id/assess" element={<ProtectedRoute><ResumeAssessment /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
        <Route path="/admin/users/:id" element={<ProtectedRoute adminOnly><AdminUserDetail /></ProtectedRoute>} />
        <Route path="/admin/chat" element={<ProtectedRoute adminOnly><AdminChat /></ProtectedRoute>} />
        <Route path="/assessment" element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
        <Route path="/assessment/result" element={<ProtectedRoute><AssessmentResult /></ProtectedRoute>} />
        <Route path="/sprint" element={<ProtectedRoute><Sprint /></ProtectedRoute>} />
        <Route path="/sprint/task1" element={<ProtectedRoute><SprintTask1 /></ProtectedRoute>} />
        <Route path="/sprint/task2" element={<ProtectedRoute><SprintTask2 /></ProtectedRoute>} />
        <Route path="/cdp" element={<ProtectedRoute><CDP /></ProtectedRoute>} />
        <Route path="/cdp/doc/:enId" element={<ProtectedRoute><CDPDocument /></ProtectedRoute>} />
        <Route path="/cdp/certificate" element={<ProtectedRoute><CDPCertificate /></ProtectedRoute>} />
        <Route path="/mock-interview" element={<ProtectedRoute><MockInterview /></ProtectedRoute>} />
        <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
        <Route path="/v2" element={<Navigate to="/" replace />} />
        <Route path="/v2/chat/:module" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </SwipeNavigator>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
